/**
 * DTF pricing calculator — <dtf-calculator> custom element.
 * Input behavior matches live (main): plain number fields recalc on input;
 * currency fields use format-on-type like assets/calculator.js.
 */
class DtfCalculator extends HTMLElement {
  static parseAmount(value) {
    if (value === '' || value == null) {
      return 0;
    }
    const parsed = Number(String(value).replace(/[$,]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  static formatResult(amount) {
    const value = Number.isFinite(amount) ? amount : 0;
    return '$' + value.toFixed(2);
  }

  static formatNumber(value) {
    return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  static isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  constructor() {
    super();
    this._abortController = null;
    this._calculate = this._calculate.bind(this);
  }

  connectedCallback() {
    this._mount();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
  }

  _mount() {
    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._bindTooltips(signal);
    this._bindInputs(signal);
    this.classList.add('is-mounted');
  }

  _bindTooltips(signal) {
    this.querySelectorAll('[data-tooltip-trigger]').forEach((trigger) => {
      const tooltip = trigger.querySelector('[data-tooltip]');
      if (!tooltip) {
        return;
      }

      trigger.addEventListener('mouseenter', () => tooltip.classList.remove('hidden'), { signal });
      trigger.addEventListener('mouseleave', () => tooltip.classList.add('hidden'), { signal });
      trigger.addEventListener('focus', () => tooltip.classList.remove('hidden'), { signal });
      trigger.addEventListener('blur', () => tooltip.classList.add('hidden'), { signal });
    });
  }

  _bindInputs(signal) {
    this.querySelectorAll('input').forEach((input) => {
      if (input.dataset.type === 'currency') {
        this._bindCurrencyInput(input, signal);
      } else {
        input.addEventListener('input', this._calculate, { signal });
        input.addEventListener('keyup', this._calculate, { signal });
      }
    });
  }

  _formatCurrency(input, blur) {
    let inputValue = input.value;

    if (inputValue === '') {
      this._calculate();
      return;
    }

    const originalLength = inputValue.length;
    let caretPos = input.selectionStart;

    if (inputValue.indexOf('.') >= 0) {
      const decimalPos = inputValue.indexOf('.');
      let leftSide = DtfCalculator.formatNumber(inputValue.substring(0, decimalPos));
      let rightSide = DtfCalculator.formatNumber(inputValue.substring(decimalPos));

      if (blur === 'blur') {
        rightSide += '00';
      }

      rightSide = rightSide.substring(0, 2);
      inputValue = '$' + leftSide + '.' + rightSide;
    } else {
      inputValue = '$' + DtfCalculator.formatNumber(inputValue);

      if (blur === 'blur') {
        inputValue += '.00';
      }
    }

    input.value = inputValue;

    const updatedLength = inputValue.length;
    caretPos = updatedLength - originalLength + caretPos;
    input.setSelectionRange(caretPos, caretPos);
  }

  _bindCurrencyInput(input, signal) {
    const onCurrencyInput = () => {
      this._formatCurrency(input);
      this._calculate();
    };

    const onCurrencyBlur = () => {
      this._formatCurrency(input, 'blur');
      this._calculate();
    };

    input.addEventListener('keyup', onCurrencyInput, { signal });
    input.addEventListener('input', onCurrencyInput, { signal });

    if (DtfCalculator.isTouchDevice()) {
      input.addEventListener('mouseout', onCurrencyBlur, { signal });
    } else {
      input.addEventListener('blur', onCurrencyBlur, { signal });
    }
  }

  _setOutput(output, amount) {
    if (output) {
      output.textContent = DtfCalculator.formatResult(amount);
    }
  }

  _setResult(field, amount) {
    this._setOutput(this.querySelector('[data-result="' + field + '"]'), amount);
  }

  _calculate() {
    const garmentCost = DtfCalculator.parseAmount(
      this.querySelector('[data-field="garment-cost"]')?.value
    );
    const primaryPrintSize = DtfCalculator.parseAmount(
      this.querySelector('[data-field="primary-print-size"]')?.value
    );
    const primaryPrintCost = DtfCalculator.parseAmount(
      this.querySelector('[data-field="primary-print-cost"]')?.value
    );
    const secondaryPrintSize = DtfCalculator.parseAmount(
      this.querySelector('[data-field="secondary-print-size"]')?.value
    );
    const secondaryPrintCost = DtfCalculator.parseAmount(
      this.querySelector('[data-field="secondary-print-cost"]')?.value
    );
    const laborSetupFee = DtfCalculator.parseAmount(
      this.querySelector('[data-field="labor-setup-fee"]')?.value
    );
    const desiredProfitPerShirt = DtfCalculator.parseAmount(
      this.querySelector('[data-field="desired-profit-per-shirt"]')?.value
    );
    const garmentOrderQuantity = DtfCalculator.parseAmount(
      this.querySelector('[data-field="garment-order-quantity"]')?.value
    );
    const customerShipping = DtfCalculator.parseAmount(
      this.querySelector('[data-field="customer-shipping"]')?.value
    );

    const primaryLineTotal =
      Math.round(primaryPrintCost * primaryPrintSize * 100) / 100;
    const secondaryLineTotal =
      Math.round(secondaryPrintCost * secondaryPrintSize * 100) / 100;

    const totalCostPerGarment =
      garmentCost +
      primaryLineTotal +
      secondaryLineTotal +
      laborSetupFee +
      desiredProfitPerShirt;
    const subtotalCost = totalCostPerGarment * garmentOrderQuantity;
    const totalOrderCost = subtotalCost + customerShipping;
    const profitFromOrder = garmentOrderQuantity * desiredProfitPerShirt;

    this._setResult('total-cost-per-garment', totalCostPerGarment);
    this._setResult('subtotal-cost', subtotalCost);
    this._setResult('total-order-cost', totalOrderCost);
    this._setResult('profit-from-order', profitFromOrder);
  }
}

if (!window.customElements.get('dtf-calculator')) {
  window.customElements.define('dtf-calculator', DtfCalculator);
}
